import { useState, useRef } from "react";
import { FileUp, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLang, CATEGORIES, catLabel } from "@/lib/i18n";
import api, { apiError } from "@/lib/api";

const shiftDate = (dt, days) => {
  if (!dt || dt.length < 16) return dt;
  const d = new Date(`${dt.slice(0, 16)}:00`);
  d.setDate(d.getDate() + days);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const ImportPdfDialog = ({ slug, assoc, mosques, open, onOpenChange, onImported }) => {
  const { t } = useLang();
  const fileRef = useRef(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [proposals, setProposals] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [shiftJ1, setShiftJ1] = useState(true);

  const reset = () => {
    setProposals(null);
    setWarnings([]);
    setAnalyzing(false);
    setCreating(false);
    setShiftJ1(true);
  };

  const analyze = async (file) => {
    if (!file) return;
    setAnalyzing(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/associations/${slug}/events/import-pdf`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setProposals(data.events.map((e) => ({
        ...e,
        start: shiftDate(e.start, -1),
        end: e.end ? shiftDate(e.end, -1) : e.end,
        checked: true,
      })));
      setShiftJ1(true);
      setWarnings(data.warnings || []);
      if (data.events.length === 0) toast.error(t("import_none_found"));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAnalyzing(false);
    }
  };

  const setField = (i, field, value) =>
    setProposals((p) => p.map((ev, idx) => (idx === i ? { ...ev, [field]: value } : ev)));

  const toggleShiftJ1 = (checked) => {
    const days = checked ? -1 : 1;
    setShiftJ1(checked);
    setProposals((p) => p.map((ev) => ({
      ...ev,
      start: shiftDate(ev.start, days),
      end: ev.end ? shiftDate(ev.end, days) : ev.end,
    })));
  };

  const confirm = async () => {
    const selected = proposals.filter((p) => p.checked);
    if (selected.length === 0) return;
    setCreating(true);
    try {
      const { data } = await api.post(`/associations/${slug}/events/import-confirm`, {
        events: selected.map(({ checked, ...ev }) => ev),
      });
      toast.success(t("import_success").replace("{n}", data.created));
      onOpenChange(false);
      reset();
      onImported?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreating(false);
    }
  };

  const nbChecked = proposals?.filter((p) => p.checked).length || 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("import_pdf_title")}</DialogTitle>
        </DialogHeader>

        {!proposals && (
          <div className="space-y-4">
            {mosques.length === 0 ? (
              <p className="text-sm text-amber-500 bg-amber-500/10 rounded-md px-3 py-3" data-testid="import-no-mosque-warning">
                ⚠ {t("import_no_mosque")}
              </p>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">{t("import_pdf_hint")}</p>
                <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden"
                  data-testid="import-pdf-file-input"
                  onChange={(e) => analyze(e.target.files?.[0])} />
                <Button onClick={() => fileRef.current?.click()} disabled={analyzing}
                  data-testid="import-pdf-select-btn" className="rounded-full w-full h-24 border-2 border-dashed"
                  variant="outline">
                  {analyzing ? (
                    <span className="flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> {t("import_analyzing")}</span>
                  ) : (
                    <span className="flex items-center gap-2"><FileUp className="h-5 w-5" /> {t("import_select_file")}</span>
                  )}
                </Button>
              </>
            )}
          </div>
        )}

        {proposals && (
          <div className="space-y-3">
            <p className="text-sm font-medium" data-testid="import-events-found">
              {t("import_events_found").replace("{n}", proposals.length)}
            </p>
            <label className="flex items-start gap-2 text-xs bg-primary/5 border border-primary/20 rounded-md px-3 py-2 cursor-pointer">
              <Checkbox checked={shiftJ1} onCheckedChange={(c) => toggleShiftJ1(!!c)}
                data-testid="import-shift-j1-checkbox" className="mt-0.5" />
              <span>{t("import_shift_j1")}</span>
            </label>
            {warnings.length > 0 && (
              <div className="text-xs text-amber-500 bg-amber-500/10 rounded-md px-3 py-2 space-y-0.5">
                {warnings.map((w, i) => <p key={i}>⚠ {w}</p>)}
              </div>
            )}
            <div className="space-y-2">
              {proposals.map((ev, i) => (
                <div key={i} data-testid="import-proposal-row"
                  className={`border rounded-lg p-3 space-y-2 ${ev.checked ? "border-border" : "border-border/40 opacity-50"}`}>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={ev.checked} onCheckedChange={(c) => setField(i, "checked", !!c)}
                      data-testid={`import-check-${i}`} />
                    <Input value={ev.title} onChange={(e) => setField(i, "title", e.target.value)}
                      className="h-8 text-sm font-medium flex-1" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pl-6">
                    <Input type="datetime-local" value={ev.start}
                      onChange={(e) => setField(i, "start", e.target.value)} className="h-8 text-xs" />
                    <Select value={ev.mosque_id} onValueChange={(v) => setField(i, "mosque_id", v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {mosques.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={ev.category} onValueChange={(v) => setField(i, "category", v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`cat_${c}`)}</SelectItem>)}
                        {(assoc?.event_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        {ev.category && !CATEGORIES.includes(ev.category) && !(assoc?.event_categories || []).includes(ev.category) && (
                          <SelectItem value={ev.category}>{catLabel(t, ev.category)}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={reset} className="rounded-full" data-testid="import-back-btn">
                {t("import_other_file")}
              </Button>
              <Button onClick={confirm} disabled={creating || nbChecked === 0} className="rounded-full flex-1 active:scale-95"
                data-testid="import-confirm-btn">
                {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                {t("import_create_n").replace("{n}", nbChecked)}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
